import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { waSupportUrl } from "@/lib/dunningBanner";
import { openExternalUrl } from "@/utils/linkify";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { SubscriptionGate } from "@/components/SubscriptionGate";

interface ChallengeAccessGateProps {
  children: React.ReactNode;
  /** Route param holding the challenge id ("challengeId" on /challenge/*, "id" on /course/*) */
  paramName?: string;
}

type Decision = "open" | "members" | "not_owned" | null;

/**
 * A course you never bought. Distinct from "members" on purpose.
 *
 * Both wrong answers were live at once and both were reported as bugs on
 * 2026-08-20: native fell through to LessonView and said "השיעור לא נמצא"
 * (the lesson exists — it is the reader who has no rights), and web said
 * "תוכן זה זמין למנויים בלבד" (a subscription would not open this course
 * either; courses are bought separately and kept forever).
 *
 * No purchase button: courses are not sold in-app, and offering a native user
 * an external checkout is the App Store 3.1.1 anti-steering pattern. A
 * SUPPORT contact is fine — and it's the only way forward for someone who did
 * buy it. It said "כתבו לנו" with nothing to tap and no way back.
 */
function CourseNotOwnedWall() {
  const navigate = useNavigate();
  return (
    <div
      dir="rtl"
      className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
    >
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
        <Lock className="w-8 h-8 text-primary" />
      </div>
      <h2 className="text-xl font-bold text-foreground mb-2">הקורס הזה לא ברשותך</h2>
      <p className="text-muted-foreground max-w-sm">
        הקורסים נרכשים בנפרד מהמנוי, ומי שרכש קורס שומר עליו לתמיד. אם רכשתם אותו
        והוא לא מופיע — כתבו לנו ונטפל בזה.
      </p>
      <div className="mt-6 flex flex-col gap-2 w-full max-w-xs">
        <Button
          className="min-h-11"
          onClick={() => void openExternalUrl(waSupportUrl("היי, רכשתי קורס ואני לא רואה אותו באפליקציה. אפשר לעזור?"))}
        >
          כתיבה אלינו בוואטסאפ
        </Button>
        <Button variant="outline" className="min-h-11" onClick={() => navigate("/library")}>
          חזרה לספרייה
        </Button>
      </div>
    </div>
  );
}

/**
 * Client-side mirror of the challenge_days RLS policy.
 *
 * The database is the real access control; this only avoids showing a broken
 * page to someone the policy will block anyway. Every uncertain path FAILS
 * OPEN — a paywall flash on a paying member is its own bug.
 */
export default function ChallengeAccessGate({
  children,
  paramName = "challengeId",
}: ChallengeAccessGateProps) {
  const params = useParams();
  const challengeId = params[paramName];
  const { user } = useAuth();
  const [decision, setDecision] = useState<Decision>(
    !challengeId || !user ? "open" : null
  );

  useEffect(() => {
    if (!challengeId || !user) {
      setDecision("open");
      return;
    }

    let cancelled = false;
    setDecision(null);

    (async () => {
      try {
        const { data, error } = await supabase
          .from("challenges")
          .select("challenge_type, membership_only")
          .eq("id", challengeId)
          .maybeSingle();

        if (cancelled) return;
        if (error || !data) {
          setDecision("open");
          return;
        }

        const type = data.challenge_type;

        // Included-with-subscription content (the lives recordings). Checked
        // BEFORE the grant lookups below, mirroring the order in
        // current_user_can_view_challenge: 2,397 users hold an entry_lives or
        // funnelkit grant on that course which no longer confers access, so
        // checking a grant first would wave them through to a page the API
        // then serves empty.
        if (data.membership_only) {
          setDecision("members");
          return;
        }

        if (type === "community") {
          setDecision("members");
          return;
        }

        if (type === "entry") {
          // Enrolment OR an explicit grant. FunnelKit paid entry purchases
          // write user_course_access and may not write an enrolment.
          const [enrollCheck, grantCheck] = await Promise.all([
            supabase
              .from("user_challenge_enrollments")
              .select("id")
              .eq("user_id", user.id)
              .eq("challenge_id", challengeId)
              .limit(1)
              .maybeSingle(),
            supabase
              .from("user_course_access")
              .select("id")
              .eq("user_id", user.id)
              .eq("challenge_id", challengeId)
              .eq("is_active", true)
              .limit(1)
              .maybeSingle(),
          ]);
          if (cancelled) return;
          setDecision(
            enrollCheck.error || enrollCheck.data || grantCheck.error || grantCheck.data
              ? "open"
              : "members"
          );
          return;
        }

        if (type === "course") {
          // Keyed on challenge_id — course_id is NULL on every active grant row.
          const { data: grant, error: grantErr } = await supabase
            .from("user_course_access")
            .select("id")
            .eq("user_id", user.id)
            .eq("challenge_id", challengeId)
            .eq("is_active", true)
            .limit(1)
            .maybeSingle();
          if (cancelled) return;
          // Fail OPEN on a query error — a paywall flash on a real owner is its
          // own bug, and the database refuses the content anyway.
          setDecision(grantErr || grant ? "open" : "not_owned");
          return;
        }

        setDecision("open");
      } catch {
        if (!cancelled) setDecision("open");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [challengeId, user?.id]);

  // A spinner, not a blank screen, while the access check runs.
  if (decision === null) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" aria-busy="true">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (decision === "not_owned") return <CourseNotOwnedWall />;
  if (decision === "members") return <SubscriptionGate>{children}</SubscriptionGate>;
  return <>{children}</>;
}
